import React from 'react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';

import { Link as LinkIcon, Settings2, FileDown } from 'lucide-react';
import SEO from '@/components/SEO';

export default function Integrations() {
  const { isMobile } = useSidebar();

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background text-foreground space-y-6">
      <SEO title="Integrations - Ankh Studio" description="Connect KnowledgeFlow with other tools and export your data" />
        <div className="flex justify-between items-center mb-6 border-b border-border pb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-2">
              <LinkIcon className="h-6 w-6 text-accent" /> Integrations & Export
            </h1>
            <p className="text-muted-foreground mt-1">Connect KnowledgeFlow with other tools and export your data.</p>
          </div>
           <div className="flex items-center gap-2 mt-4 md:mt-0">
            {!isMobile && (
              <Input placeholder="Search integrations..." className="w-full md:w-64 bg-card border-input ring-accent" />
            )}
             <Button variant="outline" size="icon">
                <Settings2 className="h-4 w-4" />
                 <span className="sr-only">Integration Settings</span>
            </Button>
            {isMobile && <SidebarTrigger />}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-card shadow-md border-border rounded-lg">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                <FileDown className="h-5 w-5 text-accent" /> Export Data
              </CardTitle>
              <CardDescription>Download your analytics and content data.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-2 text-secondary-foreground">Export Analytics</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm"><FileDown className="h-4 w-4 mr-1" /> Dashboard PDF</Button>
                  <Button variant="outline" size="sm"><FileDown className="h-4 w-4 mr-1" /> Analytics CSV</Button>
                </div>
              </div>
               <div>
                <h3 className="text-sm font-medium mb-2 text-secondary-foreground">Export Content</h3>
                 <p className="text-xs text-muted-foreground mb-2">Export saved resources or summaries (coming soon).</p>
                <Button variant="outline" size="sm" disabled><FileDown className="h-4 w-4 mr-1" /> Content Data</Button>
              </div>
               <div>
                <h3 className="text-sm font-medium mb-2 text-secondary-foreground">Scheduled Reports</h3>
                 <p className="text-xs text-muted-foreground mb-2">Receive regular email summaries of your key metrics.</p>
                <Button variant="secondary" size="sm">Configure Email Reports</Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card shadow-md border-border rounded-lg">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                <LinkIcon className="h-5 w-5 text-accent" /> Productivity Integrations
              </CardTitle>
              <CardDescription>Connect KnowledgeFlow to your workflow tools.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-3 border rounded-md bg-muted/30">
                <div className="flex items-center gap-3">
                   <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 dark:text-blue-300" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.03 15.41l-4.07-4.07 1.41-1.41 2.66 2.66 5.66-5.66 1.41 1.41-7.07 7.07z"/></svg>
                   </div>
                   <div>
                      <h4 className="font-medium text-sm">Jira</h4>
                      <p className="text-xs text-muted-foreground">Sync tasks and learning progress.</p>
                   </div>
                </div>
                 <Button variant="outline" size="sm">Connect</Button>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-md bg-muted/30">
                 <div className="flex items-center gap-3">
                     <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-800">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>
                    </div>
                   <div>
                      <h4 className="font-medium text-sm">Notion</h4>
                      <p className="text-xs text-muted-foreground">Embed resources and track notes.</p>
                   </div>
                </div>
                 <Button variant="outline" size="sm">Connect</Button>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-md bg-muted/30">
                 <div className="flex items-center gap-3">
                   <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 dark:text-green-300" fill="currentColor" viewBox="0 0 24 24"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H5V10h14v8zm0-10H5V6h14v2z"/></svg>
                   </div>
                   <div>
                      <h4 className="font-medium text-sm">Calendar</h4>
                      <p className="text-xs text-muted-foreground">Schedule learning sessions.</p>
                   </div>
                </div>
                 <Button variant="outline" size="sm">Connect</Button>
              </div>
            </CardContent>
          </Card>
        </div>
    </div>
  );
}
